import { t } from "i18next";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

import { isCalendarClusterEvent } from "./typePredicate";

import type { Truncated } from "okast-timeline";

import type { GetCalendarFillingWeekResponse } from "../api/FastAPI.types";
import type { CalendarEvent } from "../components/fast_channels/calendar/Calendar";
import type { MediaFormat } from "../constants/media";

const MIN_EVENT_DURATION_TO_NOT_CLUSTER = 5 * 60 * 1000;
const MIN_EVENT_DURATION_TO_DISPLAY = 1 * 60 * 1000;

export interface CalendarClusterEvent extends CalendarEvent {
  resource: {
    uuid: string;
    label: string;
    startTime: string;
    endTime: string;
    duration: number;
    picture: string | null;
    format: MediaFormat | null;
    scheduleId: string;
    truncated: Truncated;
    itemIdList: Set<string>;
  };
}

export type CalendarEventWithCluster = CalendarEvent | CalendarClusterEvent;

interface ClusteringList {
  itemList: CalendarEventWithCluster[];
  currentCluster?: CalendarClusterEvent;
}

const areItemsAdjacent = (
  item: CalendarEvent,
  nextItem?: CalendarEvent,
): boolean => {
  if (!nextItem) {
    return false;
  }

  const itemEnd = new Date(item.resource.endTime).valueOf();
  const nextItemStart = new Date(nextItem.resource.startTime).valueOf();

  return itemEnd === nextItemStart - 1;
};

const getNewCluster = (
  newItem: CalendarEventWithCluster,
  currentCluster?: CalendarClusterEvent,
): CalendarClusterEvent => {
  const nextItemIdList = isCalendarClusterEvent(newItem)
    ? newItem.resource.itemIdList
    : [newItem.resource.uuid];

  const startTime =
    currentCluster?.resource.startTime ?? newItem.resource.startTime;

  const { endTime } = newItem.resource;

  const itemIdList = new Set([
    ...(currentCluster?.resource.itemIdList ?? []),
    ...nextItemIdList,
  ]);

  const label = t("clusterTitle", {
    ns: "editor-fast-channel",
    count: itemIdList.size,
  });

  return {
    title: label,
    start: new Date(startTime),
    end: new Date(endTime),
    resource: {
      uuid: currentCluster?.resource.uuid ?? uuidv4(),
      label,
      startTime,
      endTime,
      duration:
        (currentCluster?.resource.duration ?? 0) + newItem.resource.duration,
      picture: null,
      format: null,
      scheduleId:
        currentCluster?.resource.scheduleId ?? newItem.resource.scheduleId,
      truncated: null,
      itemIdList,
    },
  };
};

export const mergeCalendarEventData = (
  eventList: CalendarEvent[],
): CalendarEvent[] => {
  // Reduce data to create clusters
  const clusterizedList = eventList.reduce(
    (acc: ClusteringList, currentItem, currentIndex, array) => {
      const nextItem = array[currentIndex + 1];
      const { currentCluster } = acc;

      const shouldCluster =
        areItemsAdjacent(currentItem, nextItem) &&
        currentItem.resource.duration < MIN_EVENT_DURATION_TO_NOT_CLUSTER &&
        nextItem &&
        nextItem.resource.duration < MIN_EVENT_DURATION_TO_NOT_CLUSTER;

      const isLastClusterItem =
        (!nextItem || !shouldCluster) && !!currentCluster;

      if (shouldCluster || isLastClusterItem) {
        const newCluster = getNewCluster(currentItem, currentCluster);

        acc.currentCluster = isLastClusterItem ? undefined : newCluster;

        if (isLastClusterItem) {
          acc.itemList.push(newCluster);
        }
      } else {
        acc.itemList.push(currentItem);
      }

      return acc;
    },
    { currentCluster: undefined, itemList: [] },
  ).itemList;

  // Clusterize clusters if they are next to each other
  const superClusterizedList = clusterizedList.reduce(
    (acc: ClusteringList, currentItem, currentIndex, array) => {
      const nextItem = array[currentIndex + 1];

      const canItemsBeClustered =
        isCalendarClusterEvent(currentItem) && isCalendarClusterEvent(nextItem);

      const shouldCluster =
        canItemsBeClustered && areItemsAdjacent(currentItem, nextItem);

      const isLastClusterItem =
        (!nextItem || !shouldCluster) && !!acc.currentCluster;

      if (shouldCluster || isLastClusterItem) {
        const newCluster = getNewCluster(currentItem, acc.currentCluster);

        acc.currentCluster = isLastClusterItem ? undefined : newCluster;

        if (isLastClusterItem) {
          acc.itemList.push(newCluster);
        }
      } else {
        acc.itemList.push(currentItem);
      }

      return acc;
    },
    { currentCluster: undefined, itemList: [] },
  ).itemList;

  return superClusterizedList;
};

export const getCalendarItems = (
  calendarFillingWeek?: GetCalendarFillingWeekResponse,
): CalendarEvent[] => {
  if (!calendarFillingWeek) return [];

  const eventWeekList: CalendarEvent[] = Object.values(calendarFillingWeek.data)
    .flat()
    .map((media) => {
      const interstitialAdbreaks =
        media.interstitial_adbreak_config?.adbreaks_at ||
        media.interstitial_adbreak_config?.cuepoints ||
        media.interstitial_adbreak_uuid
          ? {
              adbreakList:
                media.interstitial_adbreak_config?.adbreaks_at?.map(
                  (adbreak) => ({
                    startTime: adbreak.start_time,
                    endTime: adbreak.end_time,
                  }),
                ) ?? [],
              cuepointList: media.interstitial_adbreak_config?.cuepoints ?? [],
              adbreakId: media.interstitial_adbreak_uuid,
            }
          : undefined;

      return {
        title: media.object_label,
        start: new Date(media.start_time),
        end: new Date(media.end_time),
        resource: {
          uuid: media.uuid,
          label: media.object_label,
          startTime: media.start_time,
          endTime: media.end_time,
          duration: media.object_duration,
          picture: media.object_picture ?? null,
          format: media.object_format,
          scheduleId: media.fast_channel_schedule_uuid,
          truncated: null,
          interstitialAdbreaks,
        },
      };
    });

  const eventWeekListWithClusters = mergeCalendarEventData(eventWeekList);

  // Split events that are on two different days and filter too short events
  const eventWeekListFiltered = eventWeekListWithClusters.reduce(
    (acc: CalendarEvent[], event) => {
      const startDate = moment(event.start);
      const endDate = moment(event.end);

      if (!startDate.isSame(endDate, "day")) {
        const firstDayEvent: CalendarEvent = {
          ...event,
          end: startDate.clone().endOf("day").toDate(),
          resource: {
            ...event.resource,
            truncated: "end",
          },
        };

        const secondDayEvent: CalendarEvent = {
          ...event,
          start: endDate.clone().startOf("day").toDate(),
          resource: {
            ...event.resource,
            truncated: "start",
          },
        };

        acc.push(firstDayEvent, secondDayEvent);
      } else if (event.resource.duration >= MIN_EVENT_DURATION_TO_DISPLAY) {
        acc.push(event);
      }

      return acc;
    },
    [],
  );

  return eventWeekListFiltered;
};
